import { Dispatch, SetStateAction, useEffect, useState } from "react";

function getStorageValue<T>(key: string, defaultValue: T): T {
  if (typeof window === "undefined") return defaultValue; // 💡 SSR-safe

  try {
    const saved = localStorage.getItem(key);
    if (saved && saved !== "undefined") {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error(`Error parsing localStorage key "${key}":`, error);
  }

  return defaultValue;
}

export const useLocalStorage = <T,>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() => {
    return getStorageValue(key, defaultValue);
  });

  useEffect(() => {
    // Сохранение значения в localStorage
    if (typeof window !== "undefined") {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }, [key, value]);

  return [value, setValue];
};

export const useTemporaryStorage = <T,>(
  key: string,
  defaultValue: T,
  expirationTime: number = 24 * 60 * 60 * 1000
): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(defaultValue);

  useEffect(() => {
    const stored = getStorageValue<{ data: T; timestamp: number }>(key, {
      data: defaultValue,
      timestamp: 0,
    });

    if (Date.now() - stored.timestamp > expirationTime) {
      setValue(defaultValue);
    } else {
      setValue(stored.data);
    }
  }, [key]);

  useEffect(() => {
    const storageData = {
      data: value,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(key, JSON.stringify(storageData));
    } catch (error) {
      console.error(
        `Ошибка сохранения временных данных в localStorage для ключа "${key}":`,
        error
      );
    }
  }, [key, value]);

  return [value, setValue];
};
